import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  CardActions,
  Chip,
  Alert,
  Grid,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  AutoFixHigh as RewriteIcon,
  Psychology as AIIcon,
  PlayArrow as StartIcon
} from '@mui/icons-material';
import { dynamicContentService } from '../../services/dynamicContentService';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

interface RewriteResult {
  id: number;
  exhibition_id: number;
  original_description: string;
  cleaned_description: string;
  rewritten_description: string;
  validation_score: number;
  validation_errors: string[];
  needs_manual_review: boolean;
  status: string;
  exhibition: {
    title: string;
    metadata: any;
  };
}

const ExhibitionRewriteReview: React.FC = () => {
  const [pendingRewrites, setPendingRewrites] = useState<RewriteResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedRewrite, setSelectedRewrite] = useState<RewriteResult | null>(null);
  const [editedDescription, setEditedDescription] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [batchProgress, setBatchProgress] = useState<any>(null);
  const [needRewriteCount, setNeedRewriteCount] = useState(0);

  useEffect(() => {
    loadPendingRewrites();
    checkNeedRewrite();
  }, []);

  const loadPendingRewrites = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/dynamic-content/rewrite/pending`);
      setPendingRewrites(response.data.rewrites);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load pending rewrites');
    } finally {
      setLoading(false);
    }
  };

  const checkNeedRewrite = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/dynamic-content/rewrite/needed`);
      setNeedRewriteCount(response.data.count);
    } catch (err) {
      console.error('Error checking exhibitions needing rewrite:', err);
    }
  };

  const handleBatchRewrite = async (limit: number = 10) => {
    setLoading(true);
    setError(null);
    setBatchProgress({ status: 'processing', message: `Rewriting ${limit} exhibitions...` });
    
    try {
      const response = await axios.post(`${API_BASE_URL}/api/dynamic-content/rewrite/batch`, {
        limit
      });
      
      const { report } = response.data;
      setBatchProgress({
        status: 'complete',
        report,
        message: `Successfully rewrote ${report.successful} exhibitions!`
      });
      
      // Reload pending rewrites
      await loadPendingRewrites();
      await checkNeedRewrite();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to batch rewrite');
      setBatchProgress(null);
    } finally {
      setLoading(false);
    }
  };

  const handleReviewAction = async (rewriteId: number, action: 'approve' | 'reject' | 'edit') => {
    try {
      const payload: any = {
        action,
        reviewNotes
      };
      
      if (action === 'edit' && editedDescription) {
        payload.editedDescription = editedDescription;
      }
      
      await axios.put(`${API_BASE_URL}/api/dynamic-content/rewrite/${rewriteId}/review`, payload);
      
      setSuccess(`Exhibition ${action}ed successfully!`);
      setSelectedRewrite(null);
      setEditedDescription('');
      setReviewNotes('');
      
      // Reload the list
      await loadPendingRewrites();
    } catch (err: any) {
      setError(err.response?.data?.error || `Failed to ${action} rewrite`);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  const getScoreIcon = (score: number) => {
    if (score >= 80) return <CheckIcon />;
    if (score >= 60) return <WarningIcon />;
    return <ErrorIcon />;
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Exhibition Rewrite Review
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          {needRewriteCount > 0 && (
            <>
              <Chip
                icon={<WarningIcon />}
                label={`${needRewriteCount} need rewriting`}
                color="warning"
              />
              <Button
                variant="outlined"
                startIcon={<RewriteIcon />}
                onClick={() => handleBatchRewrite(5)}
                disabled={loading}
              >
                Rewrite 5
              </Button>
              <Button
                variant="contained"
                startIcon={<AIIcon />}
                onClick={() => handleBatchRewrite(10)}
                disabled={loading}
              >
                Rewrite 10
              </Button>
            </>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Batch Progress */}
      {batchProgress && (
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'background.default' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {batchProgress.status === 'processing' ? '🔄 Rewriting in Progress' : '✅ Rewrite Complete'}
          </Typography>
          
          {batchProgress.status === 'processing' ? (
            <>
              <LinearProgress sx={{ mb: 2 }} />
              <Typography>{batchProgress.message}</Typography>
            </>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="success.main">
                    {batchProgress.report.successful}
                  </Typography>
                  <Typography variant="body2">Successful</Typography>
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="warning.main">
                    {batchProgress.report.needsManualReview}
                  </Typography>
                  <Typography variant="body2">Need Review</Typography>
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="error.main">
                    {batchProgress.report.failed}
                  </Typography>
                  <Typography variant="body2">Failed</Typography>
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="primary.main">
                    {batchProgress.report.averageScoreAfter}
                  </Typography>
                  <Typography variant="body2">Avg Score</Typography>
                </Box>
              </Grid>
            </Grid>
          )}
        </Paper>
      )}

      {/* Pending Rewrites */}
      {loading && !batchProgress ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : pendingRewrites.length === 0 ? (
        <Alert severity="info">
          No pending rewrites to review. 
          {needRewriteCount > 0 && ` Click "Rewrite" above to process exhibitions that need updating.`}
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {pendingRewrites.map((rewrite) => (
            <Grid item xs={12} key={rewrite.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6">
                      {rewrite.exhibition.title}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Chip
                        icon={getScoreIcon(rewrite.validation_score)}
                        label={`Score: ${rewrite.validation_score}/100`}
                        color={getScoreColor(rewrite.validation_score)}
                        size="small"
                      />
                      {rewrite.needs_manual_review && (
                        <Chip
                          icon={<WarningIcon />}
                          label="Manual Review"
                          color="warning"
                          size="small"
                        />
                      )}
                    </Box>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Original ({rewrite.original_description.split(' ').length} words)
                      </Typography>
                      <Paper sx={{ p: 2, bgcolor: 'grey.50', maxHeight: 200, overflow: 'auto' }}>
                        <Typography variant="body2">
                          {rewrite.original_description}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Rewritten ({rewrite.rewritten_description.split(' ').length} words)
                      </Typography>
                      <Paper sx={{ p: 2, bgcolor: 'primary.50', maxHeight: 200, overflow: 'auto' }}>
                        <Typography variant="body2">
                          {rewrite.rewritten_description}
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>

                  {rewrite.validation_errors.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" color="error" gutterBottom>
                        Validation Issues:
                      </Typography>
                      <List dense>
                        {rewrite.validation_errors.map((error, idx) => (
                          <ListItem key={idx}>
                            <ListItemIcon>
                              <ErrorIcon color="error" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary={error} />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </CardContent>
                
                <CardActions>
                  <Button
                    size="small"
                    color="success"
                    startIcon={<CheckIcon />}
                    onClick={() => handleReviewAction(rewrite.id, 'approve')}
                  >
                    Approve
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<CloseIcon />}
                    onClick={() => handleReviewAction(rewrite.id, 'reject')}
                  >
                    Reject
                  </Button>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => {
                      setSelectedRewrite(rewrite);
                      setEditedDescription(rewrite.rewritten_description);
                    }}
                  >
                    Edit
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Edit Dialog */}
      <Dialog 
        open={!!selectedRewrite} 
        onClose={() => setSelectedRewrite(null)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>Edit Rewritten Description</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={6}
            value={editedDescription}
            onChange={(e) => setEditedDescription(e.target.value)}
            margin="normal"
            label="Description"
            helperText={`${editedDescription.split(' ').length} words (target: 60-80)`}
          />
          <TextField
            fullWidth
            multiline
            rows={2}
            value={reviewNotes}
            onChange={(e) => setReviewNotes(e.target.value)}
            margin="normal"
            label="Review Notes (optional)"
            placeholder="Any notes about the changes..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedRewrite(null)}>Cancel</Button>
          <Button 
            onClick={() => selectedRewrite && handleReviewAction(selectedRewrite.id, 'edit')} 
            variant="contained"
          >
            Save & Approve
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExhibitionRewriteReview;